import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import faviconLogo from '../../assets/logo3.png';
import carImage from '../../assets/car.png';
import carImage2 from '../../assets/car2.png';
import carImage3 from '../../assets/car3.png';
import carRideImage from '../../assets/carride.png';
import { SwapHero } from '../../components/SwapHero/SwapHero';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import worldMap from '../../assets/worldmap.jpg';
import {
  Luggage,
  Send,
  PlaneTakeoff,
  Search,
  MessagesSquare,
  ShieldCheck,
  PackageCheck,
  UsersRound,
  MessageSquareText,
  LockKeyhole,
  Star,
  RefreshCcw,
  Globe2,
  Car,
  User,
} from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, initializing } = usePublicAuth();
  const isGuest = !user && !initializing;

  // Determine mode based on URL path or localStorage
  const [isFlexyRideMode, setIsFlexyRideMode] = useState(() => {
    return (
      location.pathname.startsWith('/flexy-ride') ||
      localStorage.getItem('flexySwap_activeMode') === 'flexyRide'
    );
  });

  // Update mode when URL or localStorage changes
  useEffect(() => {
    const updateMode = () => {
      const newMode =
        location.pathname.startsWith('/flexy-ride') ||
        localStorage.getItem('flexySwap_activeMode') === 'flexyRide';
      setIsFlexyRideMode(newMode);
    };

    updateMode();

    // Listen for storage changes (when MainNavbar updates mode)
    window.addEventListener('storage', updateMode);
    window.addEventListener('flexySwap_modeChange', updateMode);

    return () => {
      window.removeEventListener('storage', updateMode);
      window.removeEventListener('flexySwap_modeChange', updateMode);
    };
  }, [location.pathname]);

  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']");
    if (link) {
      link.href = faviconLogo;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = faviconLogo;
      document.head.appendChild(newLink);
    }
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const targetId = location.state.scrollTo;
      const scroll = () => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      // Delay to ensure sections are rendered before scrolling
      const timeout = setTimeout(scroll, 150);
      navigate('.', { replace: true, state: {} });
      return () => clearTimeout(timeout);
    }
  }, [location.state, navigate]);

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-slate-950 dark:text-slate-100">
      <MainNavbar />

      <div id="home" className="pt-20">
        <SwapHero isGuest={isGuest} />
      </div>

      {isGuest && (
        <section
          id="about-flexyswap"
          className="mx-auto mt-16 max-w-6xl rounded-3xl bg-yellow-50 px-8 py-12 shadow-sm dark:bg-black"
        >
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="space-y-6">
              <p className="text-center text-sm font-semibold uppercase tracking-[0.4em] text-black dark:text-yellow-400 lg:text-left">
                FlexySwap
              </p>
              <h2 className="text-center text-4xl font-black text-black dark:text-yellow-400 lg:text-left">
                {t('home.whatIsFlexySwap')}
              </h2>
              <p className="text-center text-base text-gray-800 leading-relaxed dark:text-yellow-100 lg:text-left">
                {t('home.flexySwapDescription')}
              </p>

              <div className="space-y-8 rounded-2xl bg-black p-6 shadow-sm dark:bg-yellow-400">
                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 dark:text-black">
                    {t('home.ourMission')}
                  </h3>
                  <p className="mt-2 text-sm text-yellow-100 leading-relaxed dark:text-black">
                    {t('home.missionDescription')}
                  </p>
                </div>
                <div className="border-t border-yellow-600 pt-6 dark:border-yellow-600">
                  <h3 className="text-2xl font-bold text-yellow-400 dark:text-black">
                    {t('home.ourVision')}
                  </h3>
                  <p className="mt-2 text-sm text-yellow-100 leading-relaxed dark:text-black">
                    {t('home.visionDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src={worldMap}
                alt="Global delivery routes illustrated on a world map"
                className="w-full max-w-md rounded-3xl border-2 border-black bg-gradient-to-br from-black to-yellow-600 p-4 shadow-xl dark:border-yellow-400"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {isGuest && (
        <section
          id="problem-section"
          className="mx-auto mt-16 max-w-6xl space-y-8"
        >
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.5em] text-yellow-500 dark:text-yellow-400">
              {t('home.whyFlexySwap')}
            </p>
            <h2 className="mt-2 text-4xl font-black text-black dark:text-yellow-400">
              {t('home.theProblemWeSolve')}
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl bg-yellow-400 p-8 text-left shadow-sm dark:bg-black">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-black text-yellow-400 shadow dark:bg-yellow-400 dark:text-black">
                {isFlexyRideMode ? (
                  <Car className="h-7 w-7" aria-hidden="true" />
                ) : (
                  <Luggage className="h-7 w-7" aria-hidden="true" />
                )}
              </div>
              <h3 className="mt-6 text-2xl font-bold text-black dark:text-yellow-400">
                {isFlexyRideMode
                  ? t('home.forDrivers')
                  : t('home.forTravelers')}
              </h3>
              <p className="mt-3 text-base text-gray-800 leading-relaxed dark:text-yellow-100">
                {isFlexyRideMode
                  ? t('home.driversProblem')
                  : t('home.travelersProblem')}
              </p>
            </div>

            <div className="rounded-3xl bg-black p-8 text-left shadow-sm dark:bg-yellow-400">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-yellow-400 text-black shadow dark:bg-black dark:text-yellow-400">
                {isFlexyRideMode ? (
                  <User className="h-7 w-7" aria-hidden="true" />
                ) : (
                  <Send className="h-7 w-7" aria-hidden="true" />
                )}
              </div>
              <h3 className="mt-6 text-2xl font-bold text-yellow-400 dark:text-black">
                {isFlexyRideMode
                  ? t('home.forPassengers')
                  : t('home.forSenders')}
              </h3>
              <p className="mt-3 text-base text-yellow-100 leading-relaxed dark:text-gray-800">
                {isFlexyRideMode
                  ? t('home.passengersProblem')
                  : t('home.sendersProblem')}
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-yellow-400 px-10 py-12 text-center shadow-sm dark:bg-black">
            <p className="text-3xl font-black text-black dark:text-yellow-400">
              {t('home.theFlexySwapSolution')}
            </p>
            <p className="mt-4 text-lg text-gray-800 leading-relaxed dark:text-yellow-100">
              {isFlexyRideMode
                ? t('home.solutionDescriptionRide')
                : t('home.solutionDescription')}
            </p>
          </div>
        </section>
      )}
      {isGuest && (
        <section
          id="how-it-works"
          className="mx-auto mt-16 max-w-6xl space-y-10 rounded-3xl bg-yellow-50 px-6 py-12 text-center shadow-sm ring-2 ring-black dark:bg-black dark:ring-yellow-400"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.5em] text-black dark:text-yellow-400">
              {t('home.howItWorks')}
            </p>
            <h2 className="mt-3 text-4xl font-black text-black dark:text-yellow-400">
              {t('home.simpleSteps')}
            </h2>
            <p className="mt-2 text-base text-gray-800 dark:text-yellow-100">
              {isFlexyRideMode
                ? t('home.stepsDescriptionRide')
                : t('home.stepsDescription')}
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-5">
            {(isFlexyRideMode
              ? [
                  {
                    title: t('home.listARide'),
                    description: t('home.listARideDescription'),
                    Icon: Car,
                  },
                  {
                    title: t('home.findAMatchRide'),
                    description: t('home.findAMatchRideDescription'),
                    Icon: Search,
                  },
                  {
                    title: t('home.connectAgreeRide'),
                    description: t('home.connectAgreeRideDescription'),
                    Icon: MessagesSquare,
                  },
                  {
                    title: t('home.secureTheDealRide'),
                    description: t('home.secureTheDealRideDescription'),
                    Icon: ShieldCheck,
                  },
                  {
                    title: t('home.completeEarn'),
                    description: t('home.completeEarnDescription'),
                    Icon: PackageCheck,
                  },
                ]
              : [
                  {
                    title: t('home.listATrip'),
                    description: t('home.listATripDescription'),
                    Icon: PlaneTakeoff,
                  },
                  {
                    title: t('home.findAMatch'),
                    description: t('home.findAMatchDescription'),
                    Icon: Search,
                  },
                  {
                    title: t('home.connectAgree'),
                    description: t('home.connectAgreeDescription'),
                    Icon: MessagesSquare,
                  },
                  {
                    title: t('home.secureTheDeal'),
                    description: t('home.secureTheDealDescription'),
                    Icon: ShieldCheck,
                  },
                  {
                    title: t('home.deliverEarn'),
                    description: t('home.deliverEarnDescription'),
                    Icon: PackageCheck,
                  },
                ]
            ).map(({ title, description, Icon }, index) => (
              <div key={title} className="space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-black text-yellow-400 shadow-lg dark:bg-yellow-400 dark:text-black">
                  <span className="text-xl font-bold">{index + 1}</span>
                </div>
                <div className="space-y-2">
                  <Icon
                    className="mx-auto h-6 w-6 text-black dark:text-yellow-400"
                    aria-hidden="true"
                  />
                  <h3 className="text-xl font-semibold text-black dark:text-yellow-400">
                    {title}
                  </h3>
                  <p className="text-sm text-gray-800 leading-relaxed dark:text-yellow-100">
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {isGuest && (
        <section
          id="core-features"
          className="mx-auto mt-16 max-w-6xl space-y-8 text-center"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.5em] text-black dark:text-yellow-400">
              {t('home.ourCoreFeatures')}
            </p>
            <h2 className="mt-2 text-4xl font-black text-black dark:text-yellow-400">
              {isFlexyRideMode
                ? t('home.everythingYouNeedRide')
                : t('home.everythingYouNeed')}
            </h2>
            <p className="mt-2 text-base text-gray-800 dark:text-yellow-100">
              {isFlexyRideMode
                ? t('home.featuresDescriptionRide')
                : t('home.featuresDescription')}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {(isFlexyRideMode
              ? [
                  {
                    title: t('home.peerToPeerNetwork'),
                    description: t('home.peerToPeerDescriptionRide'),
                    Icon: UsersRound,
                  },
                  {
                    title: t('home.secureMessaging'),
                    description: t('home.secureMessagingDescription'),
                    Icon: MessageSquareText,
                  },
                  {
                    title: t('home.escrowPayment'),
                    description: t('home.escrowPaymentDescriptionRide'),
                    Icon: LockKeyhole,
                  },
                  {
                    title: t('home.reviewSystem'),
                    description: t('home.reviewSystemDescriptionRide'),
                    Icon: Star,
                  },
                  {
                    title: t('home.currencySwap'),
                    description: t('home.currencySwapDescription'),
                    Icon: RefreshCcw,
                  },
                  {
                    title: t('home.globalReach'),
                    description: t('home.globalReachDescriptionRide'),
                    Icon: Globe2,
                  },
                ]
              : [
                  {
                    title: t('home.peerToPeerNetwork'),
                    description: t('home.peerToPeerDescription'),
                    Icon: UsersRound,
                  },
                  {
                    title: t('home.secureMessaging'),
                    description: t('home.secureMessagingDescription'),
                    Icon: MessageSquareText,
                  },
                  {
                    title: t('home.escrowPayment'),
                    description: t('home.escrowPaymentDescription'),
                    Icon: LockKeyhole,
                  },
                  {
                    title: t('home.reviewSystem'),
                    description: t('home.reviewSystemDescription'),
                    Icon: Star,
                  },
                  {
                    title: t('home.currencySwap'),
                    description: t('home.currencySwapDescription'),
                    Icon: RefreshCcw,
                  },
                  {
                    title: t('home.globalReach'),
                    description: t('home.globalReachDescription'),
                    Icon: Globe2,
                  },
                ]
            ).map(({ title, description, Icon }) => (
              <div
                key={title}
                className="space-y-4 rounded-3xl bg-yellow-400 p-8 text-left shadow-sm dark:bg-black"
              >
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-black text-yellow-400 dark:bg-yellow-400 dark:text-black">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-semibold text-black dark:text-yellow-400">
                  {title}
                </h3>
                <p className="text-base text-gray-800 leading-relaxed dark:text-yellow-100">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="mx-auto mt-16 max-w-6xl lg:flex lg:items-center lg:gap-12">
        <div className="mt-10 flex-1 lg:order-2 lg:mt-0">
          <img
            src={isFlexyRideMode ? carImage3 : carImage2}
            alt={
              isFlexyRideMode
                ? 'People sharing rides'
                : 'Driver preparing packages for FlexySwap deliveries'
            }
            className="w-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 space-y-4 lg:order-1 lg:pr-6">
          <p className="text-xs font-semibold uppercase tracking-[0.5em] text-yellow-500">
            {isGuest ? t('home.swapEarn') : t('home.welcomeBack')}
          </p>
          <h2 className="text-4xl font-black text-gray-900 dark:text-slate-100">
            {isGuest ? t('home.driveWhenever') : t('home.listNextTrip')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
            {isGuest ? t('home.pickupPackages') : t('home.publishTrip')}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              className="w-full rounded-full bg-black px-5 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 sm:w-auto"
              onClick={() => {
                if (isFlexyRideMode) {
                  navigate('/flexy-ride/create-seat');
                } else {
                  navigate('/list-new-trip');
                }
              }}
            >
              {isGuest ? t('home.getStarted') : t('home.listNewTrip')}
            </button>
            <button
              className="w-full rounded-full border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 dark:border-slate-600 dark:text-slate-200 dark:hover:border-slate-400 sm:w-auto"
              onClick={() => {
                if (isGuest) {
                  navigate('/auth/login');
                } else {
                  navigate(
                    isFlexyRideMode
                      ? '/flexy-ride/my-seat-listings'
                      : '/trips/sender',
                  );
                }
              }}
            >
              {isGuest
                ? t('home.alreadyHaveAccount')
                : t('home.openSenderConsole')}
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-16 max-w-6xl lg:flex lg:items-center lg:gap-12">
        <div className="flex-1 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.5em] text-yellow-500">
            {isGuest
              ? t('home.handOffMoment')
              : isFlexyRideMode
              ? t('home.yourToolsRide')
              : t('home.yourTools')}
          </p>
          <h2 className="text-4xl font-black text-gray-900 dark:text-slate-100">
            {isGuest
              ? t('home.swapSafely')
              : isFlexyRideMode
              ? t('home.jumpBackRide')
              : t('home.jumpBack')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
            {isGuest
              ? t('home.meetUpCurbside')
              : isFlexyRideMode
              ? t('home.reviewPendingRide')
              : t('home.reviewPending')}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              className="w-full rounded-full bg-black px-5 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 sm:w-auto"
              onClick={() => {
                if (isGuest) {
                  navigate(
                    isFlexyRideMode ? '/flexy-ride/passenger' : '/trips/sender',
                  );
                } else {
                  navigate(
                    isFlexyRideMode
                      ? '/flexy-ride/my-seat-listings'
                      : '/trips/traveler',
                  );
                }
              }}
            >
              {isGuest
                ? t('home.findTraveler')
                : isFlexyRideMode
                ? t('home.viewMyRides')
                : t('home.viewMyTrips')}
            </button>
            <button
              className="w-full rounded-full border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 dark:border-slate-600 dark:text-slate-200 dark:hover:border-slate-400 sm:w-auto"
              onClick={() => {
                if (isGuest) {
                  navigate(
                    isFlexyRideMode
                      ? '/flexy-ride/create-seat'
                      : '/list-new-trip',
                  );
                } else {
                  navigate(
                    isFlexyRideMode
                      ? '/flexy-ride/my-seat-listings'
                      : '/trips/sender',
                  );
                }
              }}
            >
              {isGuest
                ? t('home.offerToCarry')
                : isFlexyRideMode
                ? t('home.manageRideRequests')
                : t('home.manageRequests')}
            </button>
          </div>
        </div>
        <div className="mt-10 flex-1 lg:mt-0">
          <img
            src={isFlexyRideMode ? carRideImage : carImage}
            alt={
              isFlexyRideMode
                ? 'People sharing rides'
                : 'Sender handing a parcel to a traveler beside a car'
            }
            className="w-full object-cover"
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
